import { AppSidebar } from "@/components/app-sidebar"
import { NavMainItem } from "@/components/nav-main";
import {
  SidebarInset,
  SidebarProvider,
} from "@/components/ui/sidebar"
import { useAuth } from "@/contexts/AuthContext";
import { ROUTE } from "@/routes/constants";
import { UserInfoResponseRole } from "@/services/generatedAPIs.schemas";
import { FileCheck, FileOutput, Library, Monitor, UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router";

// 学生端导航图标（1:1 取自 Figma 设计稿）：未选中=石板灰描边（URL，<img> 渲染）；
// 选中=主题色实心+白符号 —— 以 ?raw 内联，SVG 内 fill/stroke 用 currentColor，
// 颜色由 --sidebar-active 按主题切换（无需为每个主题各存一份彩色 SVG）。
import overviewIcon from "@/assets/nav/overview.svg";
import overviewIconActive from "@/assets/nav/overview-active.svg?raw";
import practiceIcon from "@/assets/nav/practice.svg";
import practiceIconActive from "@/assets/nav/practice-active.svg?raw";
import partnerIcon from "@/assets/nav/partner.svg";
import partnerIconActive from "@/assets/nav/partner-active.svg?raw";

export default function Dashboard() {
  const user = useAuth().getCurrentUser()!;
  const { t } = useTranslation();
  let panelItem: NavMainItem = [];
  if (user.role === UserInfoResponseRole.TEACHER) {
    panelItem = [
      {
        title: t("teacher.overview"),
        url: ROUTE.TEACHER.HOMEWORK_OVERVIEW,
        icon: Monitor
      },
      {
        title: t("teacher.homework"),
        url: ROUTE.TEACHER.OVERVIEW,
        icon: FileCheck,
        relatedUrls: [
          ROUTE.TEACHER.HOMEWORK_CLASSVIEW,
          ROUTE.TEACHER.HOMEWORK_STUDENTVIEW,
          ROUTE.TEACHER.PRACTICE_CLASSVIEW
        ]
      },
      {
        title: t("teacher.admin"),
        url: ROUTE.TEACHER.CLASS_ADMIN,
        icon: UserPlus
      },
      {
        title: t("teacher.library"),
        url: ROUTE.TEACHER.LIBRARY_EDIT,
        icon: Library,
        relatedUrls: [
          ROUTE.TEACHER.LIBRARY_TOPIC_EDIT,
          ROUTE.TEACHER.LIBRARY_QUESTION_EDIT
        ]
      },
      {
        title: t("teacher.assign"),
        url: ROUTE.TEACHER.HOMEWORK_ASSIGN,
        icon: FileOutput,
        relatedUrls: [
          ROUTE.TEACHER.HOMEWORK_MANUAL_EDIT,
          ROUTE.TEACHER.HOMEWORK_AI_EDIT,
          ROUTE.TEACHER.HOMEWORK_AI_EDIT_RESULT,
        ]
      }
    ];
  } else if (user.role === UserInfoResponseRole.STUDENT) {
    panelItem = [
      {
        title: t("student.overview"),
        url: ROUTE.STUDENT.OVERVIEW,
        iconImg: overviewIcon,
        iconImgActive: overviewIconActive
      },
      {
        title: t("student.practice"),
        url: ROUTE.STUDENT.PRACTICE_HOME,
        relatedUrls: [
          ROUTE.STUDENT.PRACTICE_EDIT,
        ],
        iconImg: practiceIcon,
        iconImgActive: practiceIconActive
      },
      {
        title: t("student.aibot"),
        url: ROUTE.STUDENT.SELF_ASSESS,
        iconImg: partnerIcon,
        iconImgActive: partnerIconActive
      }
    ];
  }

  // Figma 520:185(1920) sidebar 实测:x=24 / w=415 / r=50,即 24px 浮动 + r=50。
  // 只对学生端按 Figma 还原(m-6 + rounded-[50px]),其他角色保留旧样式(m-3 + rounded-[28px]),
  // 因 Teacher/Admin/Expert 不在本次 Figma 重做范围 [[project-figma-redesign-scope]]。
  const isStudent = user.role === UserInfoResponseRole.STUDENT;
  const sidebarClass = isStudent
    ? "flex flex-1 max-w-[21.6%] m-6 rounded-[50px] shadow-sm"
    : "flex flex-1 max-w-[21.6%] m-3 rounded-[28px] shadow-sm";

  return (


    <SidebarProvider >
      {/* 侧边栏宽度按 Figma：1920px 设计稿下为 415px（21.6%），随视口等比缩放 */}
      <AppSidebar className={sidebarClass} collapsible="none" user={user} panelItem={panelItem} />
      {/* 学生端各页面已改造为 ContentLayout md:h-full md:overflow-hidden + 内容区局部滚动,
          SidebarInset 自身在 md+ 永远不滚, 不再需要 [scrollbar-gutter:stable] 预留位
          (之前预留 ~16px 假占位带会被误以为是常驻滚动条). 局部滚动容器走 overflow-y-auto,
          只在内容溢出时才出现真实滚动条. */}
      <SidebarInset className="flex max-w-[78.4%]">
        <Outlet />
      </SidebarInset>
    </SidebarProvider>

  )
}
